// src/components/admin/DevolucionesPanel.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Button from "./Button";
import Select from "./Select";
import TextInput from "./TextInput";
import FieldLabel from "./FieldLabel";
import DevolucionEstadoSelect from "./DevolucionEstadoSelect";
import { API, fetchJson, headersAuth } from "../Utiles/api";
import {
  buildNombreCompleto,
  codeFromRow,
  deduceDevEstadoNum,
  defaultPeriodoHoy,
  fmtMoney,
  pickIban,
  pickLocalidad,
  pickNif,
  pickProvincia,
  toColegiadoCodigo,
} from "../Utiles/helpers";
import { Loader2, Filter, Download, Pencil, X } from "lucide-react";

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const getPageMeta = (pageData) => {
  // Spring Data default:
  const number = safeNum(pageData?.number) ?? safeNum(pageData?.pageNumber) ?? 0;
  const size = safeNum(pageData?.size) ?? safeNum(pageData?.pageSize) ?? null;

  // total elements:
  const totalElements =
    safeNum(pageData?.totalElements) ??
    safeNum(pageData?.total_elements) ??
    safeNum(pageData?.total) ??
    safeNum(pageData?.totalItems) ??
    safeNum(pageData?.total_items) ??
    null;

  // total pages:
  const totalPages =
    safeNum(pageData?.totalPages) ??
    safeNum(pageData?.total_pages) ??
    safeNum(pageData?.pages) ??
    (totalElements != null && size ? Math.max(1, Math.ceil(totalElements / size)) : null);

  // content:
  const content =
    Array.isArray(pageData?.content)
      ? pageData.content
      : Array.isArray(pageData?.items)
        ? pageData.items
        : Array.isArray(pageData?.data)
          ? pageData.data
          : [];

  return { number, size, totalElements, totalPages, content };
};

const normalizeColegiado = (det) => {
  if (!det || typeof det !== "object") return null;
  return {
    nombre: det.nombre ?? "",
    apellido_1: det.apellido_1 ?? det.apellido1 ?? "",
    apellido_2: det.apellido_2 ?? det.apellido2 ?? "",
    localidad: det.localidad ?? "",
    provincia: det.provincia ?? "",
    nif: det.nif ?? det.dni ?? det.NIF ?? "",
    iban:
      (det.iban ?? det.IBAN ?? det.cuenta_Bancaria ?? det.cuenta_bancaria ?? "")
        ?.toString()
        .replace(/\s+/g, "")
        .toUpperCase() || "",
  };
};

const DevolucionesPanel = ({
  devolPeriodo,
  setDevolPeriodo,
  devolFiltro,
  setDevolFiltro,
  codigoBuscar,
  setCodigoBuscar,
  toast,
  setToast,
}) => {
  const [loadingDevols, setLoadingDevols] = useState(false);
  const [devoluciones, setDevoluciones] = useState([]);

  // paginación
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(null); // puede ser null si backend no lo manda
  const [totalPages, setTotalPages] = useState(1);

  // selección múltiple
  const [sel, setSel] = useState(new Set());
  const allSelected = devoluciones.length > 0 && sel.size === devoluciones.length;
  const toggleAll = () =>
    setSel((s) => (s.size === devoluciones.length ? new Set() : new Set(devoluciones.map((x) => x.id))));
  const toggleOne = (id) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const [bulkEstado, setBulkEstado] = useState(0);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProg, setBulkProg] = useState({ done: 0, total: 0 });

  // Justificante por fila
  const [receiptFlags, setReceiptFlags] = useState({});

  // ======== EDICIÓN ========
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ periodo: "", importe: "", motivo: "" });

  const openEdit = (row) => {
    setEditErr("");
    setEditRow(row);
    setEditForm({
      periodo: (row?.periodo ?? "").toString(),
      importe:
        row?.importe == null
          ? ""
          : Number.isFinite(+row.importe)
            ? String(+row.importe)
            : String(row.importe),
      motivo: (row?.motivo ?? "").toString(),
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditOpen(false);
    setEditErr("");
    setEditRow(null);
  };

  const periodoEfectivo = (p) => {
    const v = (p || "").trim();
    return v && /^20\d{2}Q[1-4]$/.test(v) ? v : defaultPeriodoHoy();
  };

  const validarEdit = () => {
    const p = (editForm.periodo || "").trim();
    if (!/^20\d{2}Q[1-4]$/.test(p)) return "Periodo inválido. Formato: YYYYQn (ej. 2025Q4).";

    const impRaw = (editForm.importe ?? "").toString().trim().replace(",", ".");
    if (impRaw === "") return "El importe es obligatorio.";
    const imp = Number(impRaw);
    if (!Number.isFinite(imp)) return "Importe inválido.";
    if (imp < 0) return "El importe no puede ser negativo.";

    return "";
  };

  const guardarEdicionDevolucion = async () => {
    if (!editRow?.id) return;
    const msg = validarEdit();
    if (msg) {
      setEditErr(msg);
      return;
    }

    const body = {
      periodo: (editForm.periodo || "").trim(),
      motivo: (editForm.motivo || "").trim(),
      importe: Number((editForm.importe ?? "").toString().trim().replace(",", ".")),
    };

    setEditSaving(true);
    setEditErr("");

    try {
      let updated = null;
      try {
        updated = await fetchJson(`/api/remesas/devoluciones/${editRow.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      } catch {
        updated = await fetchJson(`/api/remesas/devoluciones/${editRow.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
      }

      setDevoluciones((arr) =>
        arr.map((d) => {
          if (d.id !== editRow.id) return d;
          const merged = { ...d, ...body, ...(updated && typeof updated === "object" ? updated : {}) };
          return {
            ...merged,
            periodo: merged.periodo ?? body.periodo,
            motivo: merged.motivo ?? body.motivo,
            importe: merged.importe ?? body.importe,
          };
        })
      );

      setToast(`Devolución #${editRow.id}: datos actualizados`);
      setEditOpen(false);
      setEditRow(null);
    } catch (e) {
      console.error(e);
      setEditErr(e?.message || "No se pudo guardar la edición.");
    } finally {
      setEditSaving(false);
    }
  };

  // =========================
  // ✅ ENRIQUECIDO BULK
  // =========================
  const colegiadoCache = useRef(new Map());

  const fetchColegiadosBulk = useCallback(async (codigos) => {
    const ids = [...new Set((codigos || []).map((x) => String(x).trim()).filter(Boolean))];
    if (!ids.length) return new Map();

    const idsNum = ids.map((s) => Number(s)).filter((n) => Number.isFinite(n));
    const payloadArray = idsNum.length ? idsNum : ids;

    // Intento #1: { codigos: [...] }
    try {
      const res = await fetchJson(`/api/remesas/admin/colegiados/bulk`, {
        method: "POST",
        body: JSON.stringify({ codigos: payloadArray }),
      });
      const map = new Map();
      if (res && typeof res === "object") {
        for (const [k, v] of Object.entries(res)) map.set(String(k), normalizeColegiado(v));
      }
      return map;
    } catch (e1) {
      // Intento #2: array plano [...]
      try {
        const res2 = await fetchJson(`/api/remesas/admin/colegiados/bulk`, {
          method: "POST",
          body: JSON.stringify(payloadArray),
        });
        const map2 = new Map();
        if (res2 && typeof res2 === "object") {
          for (const [k, v] of Object.entries(res2)) map2.set(String(k), normalizeColegiado(v));
        }
        return map2;
      } catch (e2) {
        console.error("[bulk] falló en ambos formatos:", e1?.message, e2?.message);
        throw e2;
      }
    }
  }, []);

  const enrichRowsWithColegiado = useCallback(
    async (rows) => {
      const codes = [
        ...new Set(
          (rows || [])
            .map((r) => (typeof toColegiadoCodigo === "function" ? toColegiadoCodigo(r) : codeFromRow(r)))
            .filter((v) => v != null && v !== "")
            .map((v) => String(v))
        ),
      ];

      const missing = codes.filter((c) => !colegiadoCache.current.has(c));
      if (missing.length) {
        try {
          const fetchedMap = await fetchColegiadosBulk(missing);
          for (const c of missing) {
            colegiadoCache.current.set(c, fetchedMap.get(c) ?? null);
          }
        } catch (e) {
          // para no reintentar infinito
          for (const c of missing) colegiadoCache.current.set(c, null);
        }
      }

      return (rows || []).map((d) => {
        const codeRaw = (typeof toColegiadoCodigo === "function" ? toColegiadoCodigo(d) : codeFromRow(d)) ?? null;
        const code = codeRaw != null && codeRaw !== "" ? String(codeRaw) : null;
        const det = code ? colegiadoCache.current.get(code) : null;

        const nombreDet = det
          ? `${det.nombre ?? ""} ${det.apellido_1 ?? ""} ${det.apellido_2 ?? ""}`.trim()
          : "";

        const nombreFila = buildNombreCompleto(d);
        const nombreCompleto = (d.nombreCompleto || d.nombre_completo || nombreFila || nombreDet || "—")
          .toString()
          .trim();

        const localidad = d.localidad || det?.localidad || pickLocalidad(d) || "—";
        const provincia = d.provincia || det?.provincia || pickProvincia(d) || "—";
        const nif = pickNif(d) || det?.nif || "";
        const ibanNorm =
          (pickIban(d) || det?.iban || "")
            ?.toString()
            ?.replace(/\s+/g, "")
            ?.toUpperCase() || "";

        return {
          ...d,
          colegiadoCodigo: code ?? d.colegiadoCodigo ?? d.colegiado_codigo ?? null,
          nombreCompleto,
          localidad,
          provincia,
          nif,
          iban: ibanNorm,
        };
      });
    },
    [fetchColegiadosBulk]
  );

  // =========================
  // ✅ LISTADO PAGINADO
  // =========================
  const listarDevoluciones = useCallback(
    async (periodo, filtro, codigo, pageArg = page, sizeArg = pageSize) => {
      try {
        setLoadingDevols(true);

        const qs = new URLSearchParams();
        if (filtro === "PEND") {
          qs.set("pendientes", "true");
        } else if (filtro === "PAG") {
        }
        if (periodo) qs.set("periodo", periodo);
        if (codigo) qs.set("colegiado", codigo);
        qs.set("page", String(pageArg));
        qs.set("size", String(sizeArg));

        const pageData = await fetchJson(`/api/remesas/devoluciones/paged?${qs.toString()}`);
        const meta = getPageMeta(pageData);
        let content = meta.content || [];
        if (filtro === "PAG") {
          content = content.filter((d) => {
            const est = deduceDevEstadoNum(d);
            return est === 1 || est === 2; // PAGADA o COMPENSADO_DEVOLUCION
          });
        }

        // ✅ aquí estaba tu problema: totalElements venía undefined -> te quedaba 0
        setPage(meta.number ?? pageArg);
        if (meta.size != null) setPageSize(meta.size);

        setTotal(meta.totalElements); // puede ser null, NO lo fuerces a 0
        setTotalPages(meta.totalPages ?? Math.max(1, (meta.content?.length ? pageArg + 2 : 1)));

        const enriched = await enrichRowsWithColegiado(content);

        setDevoluciones(enriched);
        setSel(new Set());

        const flags = {};
        for (const it of enriched) flags[it.id] = false;
        setReceiptFlags(flags);

        return enriched;
      } catch (e) {
        console.error(e);
        setToast(e.message || "Error cargando devoluciones");
        setDevoluciones([]);
        setTotal(null);
        setTotalPages(1);
        return [];
      } finally {
        setLoadingDevols(false);
      }
    },
    [enrichRowsWithColegiado, page, pageSize, setToast]
  );

  // =========================
  // ✅ ACCIONES DE CARGA
  // =========================
  const generarDevolucionesPara = useCallback(
    async (periodo) => {
      const p = periodoEfectivo(periodo);
      try {
        const res = await fetchJson(`/api/remesas/parcial/devoluciones`, {
          method: "POST",
          body: JSON.stringify({ periodo: p }),
        });
        if (res?.numDevoluciones > 0) setToast(`Generadas ${res.numDevoluciones} devoluciones (periodo ${p}).`);
        return p;
      } catch (e) {
        console.error(e);
        setToast(e.message || "No se pudieron generar las devoluciones.");
        return p;
      }
    },
    [setToast]
  );

  const fetchDevoluciones = async () => {
    const p = await generarDevolucionesPara(devolPeriodo);
    setPage(0);
    await listarDevoluciones(p, devolFiltro, codigoBuscar, 0, pageSize);
  };

  const fetchDevolucionesHistorico = async () => {
    try {
      setLoadingDevols(true);
      await fetchJson(`/api/remesas/devoluciones/generar-todo`, {
        method: "POST",
        body: JSON.stringify({ soloFaltantes: true }),
      });
      setToast("Histórico generado. Cargando primera página…");
      setPage(0);
      await listarDevoluciones(null, devolFiltro, codigoBuscar, 0, pageSize);
    } catch (e) {
      console.error(e);
      setToast(e.message || "No se pudo generar/cargar el histórico.");
    } finally {
      setLoadingDevols(false);
    }
  };

  // =========================
  // ✅ PRIMERA CARGA
  // =========================
  useEffect(() => {
    const filtroDefault = "PEND";
    const codigoDefault = "";

    if (devolFiltro !== filtroDefault) setDevolFiltro(filtroDefault);
    if (codigoBuscar !== codigoDefault) setCodigoBuscar(codigoDefault);

    setPage(0);
    listarDevoluciones(devolPeriodo, filtroDefault, codigoDefault, 0, pageSize);
  }, []);

  // ✅ Cambio de página / size => recarga SOLO esa página
  useEffect(() => {
    listarDevoluciones(devolPeriodo, devolFiltro, codigoBuscar, page, pageSize);
  }, [page, pageSize]);

  // =========================
  // ✅ Estado por fila
  // =========================
  const updateEstadoDevolucion = async (id, code, prevEstadoNum, filaData) => {
    try {
      await fetchJson(`/api/remesas/devoluciones/${id}/estado`, {
        method: "PATCH",
        body: JSON.stringify({ estado: code }),
      });
      setToast(`Devolución #${id}: estado actualizado`);
      setDevoluciones((arr) => arr.map((d) => (d.id === id ? { ...d, estado: code } : d)));

      const flagged = !!receiptFlags[id];
      if (flagged && code === 1 && (prevEstadoNum === 0 || prevEstadoNum === 2)) {
        await generarJustificanteFila(filaData);
        setReceiptFlags((m) => ({ ...m, [id]: false }));
      }
    } catch (e) {
      setToast(e.message || "Error actualizando estado");
    }
  };

  const generarJustificanteFila = async (d) => {
    try {
      const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
      const pdfDoc = await PDFDocument.create();
      const pagePdf = pdfDoc.addPage([595.28, 841.89]);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const margin = 50;
      let y = pagePdf.getHeight() - margin;

      const draw = (text, { x = margin, y: yy = y, size = 12, bold = false } = {}) =>
        pagePdf.drawText(String(text ?? ""), { x, y: yy, size, font: bold ? fontBold : font, color: rgb(0, 0, 0) });

      const fecha = new Date();
      const code = d.colegiadoCodigo ?? codeFromRow(d) ?? "—";

      draw("COLEGIO OFICIAL DE ENFERMERÍA DE VALLADOLID", { bold: true, size: 18 });
      y -= 18;
      draw("Justificante de abono por reembolso", { bold: true, size: 16 });
      y -= 10;
      pagePdf.drawLine({ start: { x: margin, y }, end: { x: pagePdf.getWidth() - margin, y }, thickness: 1, color: rgb(0, 0, 0) });
      y -= 24;

      draw(`Fecha: ${fecha.toLocaleDateString("es-ES")} ${fecha.toLocaleTimeString("es-ES")}`);
      y -= 14;
      draw(`Código colegiado: ${code}`);
      y -= 14;
      draw(`Nombre: ${d.nombreCompleto || "—"}`);
      y -= 14;
      draw(`NIF: ${d.nif || "—"}`);
      y -= 24;

      draw("Detalle del abono:", { bold: true });
      y -= 16;
      draw("Periodo", { bold: true });
      draw("Concepto", { x: margin + 120, bold: true });
      draw("Importe (€)", { x: pagePdf.getWidth() - margin - 100, bold: true });
      y -= 12;
      pagePdf.drawLine({ start: { x: margin, y }, end: { x: pagePdf.getWidth() - margin, y }, thickness: 0.5, color: rgb(0, 0, 0) });
      y -= 16;

      draw(d.periodo ?? "—", { x: margin, y });
      draw(d.motivo || "-", { x: margin + 120, y });
      draw(fmtMoney(Number(d.importe || 0)), { x: pagePdf.getWidth() - margin - 100, y });

      const bytes = await pdfDoc.save();
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `justificante_abono_${code ?? "colegiado"}_${d.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
      }, 800);
      setToast("Justificante generado.");
    } catch (err) {
      console.error(err);
      setToast("No se pudo generar el justificante.");
    }
  };

  const aplicarEstadoBulk = async () => {
    const ids = Array.from(sel);
    if (!ids.length) return setToast("Selecciona devoluciones");

    setBulkRunning(true);
    setBulkProg({ done: 0, total: ids.length });

    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      try {
        await fetchJson(`/api/remesas/devoluciones/${id}/estado`, {
          method: "PATCH",
          body: JSON.stringify({ estado: bulkEstado }),
        });
        setDevoluciones((arr) => arr.map((d) => (d.id === id ? { ...d, estado: bulkEstado } : d)));
      } catch (e) {
        console.error("Fallo actualizando devolución", id, e);
      } finally {
        setBulkProg({ done: i + 1, total: ids.length });
      }
    }

    setBulkRunning(false);
    setSel(new Set());
    setToast(`Aplicado estado a ${ids.length} devoluciones`);
  };

  const exportarReembolsos = async () => {
    try {
      const rows = devoluciones.map((d) => ({
        ID: d.id ?? "",
        "Código colegiado": d.colegiadoCodigo ?? codeFromRow(d) ?? "",
        "Nombre completo": (d.nombreCompleto || "").trim(),
        NIF: d.nif || "",
        IBAN: d.iban || "",
        Localidad: d.localidad || "",
        Provincia: d.provincia || "",
        Periodo: d.periodo ?? "",
        Importe: Number.isFinite(+d.importe) ? +d.importe : "",
        Motivo: d.motivo ?? "",
        Calculada: d.calculadaEl
          ? new Date(d.calculadaEl).toLocaleString()
          : d.calculada_el
            ? new Date(d.calculada_el).toLocaleString()
            : "",
        Estado: ["PENDIENTE", "PAGADA", "COMPENSADO_DEVOLUCION"][deduceDevEstadoNum(d)] || "",
      }));

      const XLSX = (await import("xlsx")).default || (await import("xlsx"));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Reembolsos");
      XLSX.writeFile(wb, "reembolsos.xlsx");
    } catch (e) {
      console.error(e);
      setToast("No se pudo exportar a Excel.");
    }
  };

  const colSpanTotal = 15;
  const canPrev = !loadingDevols && page > 0;
  const canNext = !loadingDevols && page + 1 < (totalPages || 1);

  return (
    <div className="bg-white rounded-2xl shadow p-4 space-y-4">
      {/* Filtros / acciones */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <FieldLabel label="Periodo" hint="YYYYQn" />
          <TextInput
            value={devolPeriodo}
            onChange={(e) => setDevolPeriodo(e.target.value)}
            placeholder={defaultPeriodoHoy()}
            className="w-36"
          />
        </div>

        <div>
          <FieldLabel label="Estado" />
          <Select value={devolFiltro} onChange={(e) => setDevolFiltro(e.target.value)} className="w-40">
            <option value="PEND">Pendientes</option>
            <option value="PAG">Pagadas</option>
            <option value="ALL">Todas</option>
          </Select>
        </div>

        <div>
          <FieldLabel label="Cód. colegiado (opcional)" />
          <TextInput
            className="w-40"
            value={codigoBuscar}
            onChange={(e) => setCodigoBuscar(e.target.value)}
            placeholder="1234"
          />
        </div>

        <Button
          variant="outline"
          onClick={fetchDevoluciones}
          disabled={loadingDevols}
          iconLeft={loadingDevols ? <Loader2 className="animate-spin w-4 h-4" /> : <Filter className="w-4 h-4" />}
        >
          Buscar
        </Button>

        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            onClick={fetchDevolucionesHistorico}
            disabled={loadingDevols}
            iconLeft={loadingDevols ? <Loader2 className="animate-spin w-4 h-4" /> : <Filter className="w-4 h-4" />}
            title="Genera devoluciones de todos los periodos (idempotente) y lista todo"
          >
            Cargar todo (histórico)
          </Button>
          <Button variant="outline" iconLeft={<Download size={16} />} onClick={exportarReembolsos}>
            Exportar
          </Button>
        </div>
      </div>

      {/* Acciones en bloque */}
      <div className="flex items-center gap-2">
        <FieldLabel label="Acción en bloque" />
        <DevolucionEstadoSelect value={bulkEstado} onChange={setBulkEstado} className="w-[220px]" />
        <Button
          variant="outline"
          onClick={aplicarEstadoBulk}
          disabled={bulkRunning}
          iconLeft={bulkRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        >
          Aplicar a seleccionados
        </Button>
        {bulkRunning && <span className="text-sm text-gray-500">{bulkProg.done}/{bulkProg.total}</span>}
      </div>

      {/* Tabla */}
      <div className="overflow-auto">
        <div className="flex items-center gap-2 justify-end text-sm">
          <span className="text-gray-500">
            Total: {total == null ? "—" : total}
          </span>

          <select
            className="border rounded-md px-2 py-1"
            value={pageSize}
            onChange={(e) => {
              const s = Number(e.target.value);
              setPageSize(s);
              setPage(0);
            }}
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>{n}/pág</option>
            ))}
          </select>

          <button
            className="border rounded-md px-2 py-1 disabled:opacity-50"
            disabled={!canPrev}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← Anterior
          </button>

          <span className="px-2">
            Página {page + 1} / {totalPages || 1}
          </span>

          <button
            className="border rounded-md px-2 py-1 disabled:opacity-50"
            disabled={!canNext}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente →
          </button>
        </div>

        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th><input type="checkbox" onChange={toggleAll} checked={allSelected} /></th>
              <th>ID</th>
              <th>Colegiado</th>
              <th>Nombre</th>
              <th>NIF</th>
              <th>IBAN</th>
              <th>Localidad</th>
              <th>Provincia</th>
              <th>Periodo</th>
              <th className="text-right">Importe</th>
              <th>Motivo</th>
              <th>Calculada</th>
              <th>Estado</th>
              <th>Justificante</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loadingDevols && (
              <tr>
                <td colSpan={colSpanTotal} className="py-8 text-center text-gray-500">Cargando…</td>
              </tr>
            )}

            {!loadingDevols && devoluciones.length === 0 && (
              <tr>
                <td colSpan={colSpanTotal} className="py-8 text-center text-gray-400">Sin devoluciones</td>
              </tr>
            )}

            {devoluciones.map((d) => {
              const seleccionado = sel.has(d.id);
              const estadoNum = deduceDevEstadoNum(d);
              const code = d.colegiadoCodigo ?? toColegiadoCodigo(d) ?? codeFromRow(d);

              return (
                <tr key={d.id} className="border-t">
                  <td><input type="checkbox" checked={seleccionado} onChange={() => toggleOne(d.id)} /></td>
                  <td>{d.id}</td>
                  <td>{code ?? "—"}</td>
                  <td>{d.nombreCompleto || "—"}</td>
                  <td>{d.nif || "—"}</td>
                  <td className="font-mono text-xs" title={d.iban || ""}>{d.iban || "—"}</td>
                  <td>{d.localidad || "—"}</td>
                  <td>{d.provincia || "—"}</td>
                  <td>{d.periodo}</td>
                  <td className="text-right">{fmtMoney(d.importe)}</td>
                  <td className="max-w-[40ch] truncate" title={d.motivo || ""}>{d.motivo}</td>
                  <td>
                    {d.calculadaEl
                      ? new Date(d.calculadaEl).toLocaleString()
                      : d.calculada_el
                        ? new Date(d.calculada_el).toLocaleString()
                        : ""}
                  </td>
                  <td>
                    <DevolucionEstadoSelect
                      value={estadoNum}
                      onChange={(codeVal) => updateEstadoDevolucion(d.id, codeVal, estadoNum, d)}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={!!receiptFlags[d.id]}
                      onChange={(e) => setReceiptFlags((m) => ({ ...m, [d.id]: e.target.checked }))}
                    />
                  </td>
                  <td className="text-center">
                    <button
                      className="inline-flex items-center justify-center rounded-md border px-2 py-1 hover:bg-gray-50"
                      onClick={() => openEdit(d)}
                      title="Editar devolución"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MODAL edición */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="font-semibold">Editar devolución #{editRow?.id ?? "—"}</div>
              <button className="rounded-md p-2 hover:bg-gray-100" onClick={closeEdit}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 py-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <FieldLabel label="Periodo" hint="YYYYQn" />
                  <TextInput
                    value={editForm.periodo}
                    onChange={(e) => setEditForm((f) => ({ ...f, periodo: e.target.value }))}
                    placeholder={defaultPeriodoHoy()}
                  />
                </div>

                <div>
                  <FieldLabel label="Importe" />
                  <TextInput
                    value={editForm.importe}
                    onChange={(e) => setEditForm((f) => ({ ...f, importe: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <FieldLabel label="Motivo" />
                <TextInput
                  value={editForm.motivo}
                  onChange={(e) => setEditForm((f) => ({ ...f, motivo: e.target.value }))}
                />
              </div>

              {editErr && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {editErr}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
              <Button variant="outline" onClick={closeEdit} disabled={editSaving}>Cancelar</Button>
              <Button onClick={guardarEdicionDevolucion} disabled={editSaving}
                iconLeft={editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              >
                Guardar cambios
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevolucionesPanel;